import React, { useEffect } from "react";
import { useSeo } from "@/hooks/useSeo";
import { FundingCtaProvider } from "@/components/funding/FundingCta";
import FundingIntroBar from "@/components/funding/FundingIntroBar";
import ApplicantHero from "@/components/funding/ApplicantHero";
import WhoWeAre from "@/components/funding/WhoWeAre";
import PanelRouting from "@/components/funding/PanelRouting";
import First48 from "@/components/funding/First48";
import ProductCards from "@/components/funding/ProductCards";
import EligibilityTeaser from "@/components/funding/EligibilityTeaser";
import OutcomeStories from "@/components/funding/OutcomeStories";
import TrustTech from "@/components/funding/TrustTech";
import FundingFaq, { FAQS } from "@/components/funding/FundingFaq";
import FundingClose from "@/components/funding/FundingClose";
import FundingStickyCta from "@/components/funding/FundingStickyCta";
import SectorStrip from "@/components/funding/SectorStrip";
import { SECTORS } from "@/components/funding/sectors";

const GENERAL_SEO = {
  title: "Business funding, explained — Connexis",
  desc: "How Connexis takes one application to a panel of UK funders: what happens hour by hour, what it costs "
      + "you (nothing), whether it touches your credit file, and answers to the questions every business asks.",
};

/** The FAQ structured data Google reads — the rest of the head is the shared useSeo hook's job. */
function useFaqSchema(faqs) {
  useEffect(() => {
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "funding-faq-schema";
    ld.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    document.head.appendChild(ld);
    return () => ld.remove();
  }, [faqs]);
}

/**
 * The funding page, in five editions: the general one at /business-funding and four sector editions
 * on their own keyword URLs. Every edition accepts an introducer slug and feeds the same application.
 */
export default function BusinessFunding({ sector: key }) {
  const sector = key ? SECTORS[key] : null;
  const faqs = React.useMemo(() => [...(sector?.faqs || []), ...FAQS], [sector]);
  const seo = sector?.seo || GENERAL_SEO;

  // An introducer's copy of a page is the same page — point search engines at the clean URL.
  useSeo({
    title: seo.title,
    description: seo.desc,
    image: `${window.location.origin}/og/business-funding.jpg`,
    canonical: `${window.location.origin}${sector?.path || "/business-funding"}`,
  });
  useFaqSchema(faqs);

  return (
    <FundingCtaProvider>
      <main className="relative bg-[#0B1220] text-slate-300 overflow-hidden"
            data-testid={sector ? `funding-sector-${sector.key}` : "business-funding"}>
        <FundingIntroBar />
        <ApplicantHero sector={sector} />
        <WhoWeAre />
        <PanelRouting />
        <First48 />
        <ProductCards lede={sector?.productsLede} />
        <EligibilityTeaser />
        <OutcomeStories stories={sector?.stories} sector={sector} />
        <TrustTech />
        <FundingFaq items={faqs} />
        <SectorStrip current={sector?.key} />
        <FundingClose close={sector?.close} />
        <FundingStickyCta />
      </main>
    </FundingCtaProvider>
  );
}
